import React, { useState, useMemo, useRef } from "react";
import {
  ShieldCheck, Lock, BrickWall, Monitor, Settings, DoorOpen, EyeOff, KeyRound,
  Fingerprint, UsersRound, Crown, ScrollText, FolderTree, Key, Search, Users,
  HardDrive, HardDriveDownload, UserCog, DatabaseBackup, Clock, Boxes, Database,
  Server, AlertTriangle, Globe, Activity, FileText, Smartphone, Laptop, Cpu, Wifi,
  Crosshair, AlertOctagon, Terminal, Trash2, Home, Cloud, Mail, ShieldAlert,
} from "lucide-react";
import { TbX } from "react-icons/tb";
import { useDismissable } from "../../hooks/useDismissable";
import "./Settings.css";

// The icon set, keyed by the name that gets stored.
//
// lucide-react, not react-icons, even though the rest of v2 uses react-icons: the icon
// is persisted as a bare string like "HardDriveDownload", the seed data is already full
// of lucide names, and the report generator reads the same column. Switching libraries
// would orphan every stored value.
export const ICONS = {
  Activity, AlertOctagon, AlertTriangle, Boxes, BrickWall, Cloud, Clock, Cpu, Crosshair,
  Crown, Database, DatabaseBackup, DoorOpen, EyeOff, FileText, Fingerprint, FolderTree,
  Globe, HardDrive, HardDriveDownload, Home, Key, KeyRound, Laptop, Lock, Mail, Monitor,
  Search, Server, Settings, ShieldAlert, ShieldCheck, ScrollText, Smartphone, Terminal,
  Trash2, UserCog, Users, UsersRound, Wifi,
};

const NAMES = Object.keys(ICONS).sort();

// Renders a stored icon name. Everything that shows an icon goes through this, so an
// unknown name — a value written before this list existed, or by the old admin console —
// degrades to nothing rather than throwing.
export const CategoryIcon = ({ name, size = 15, className }) => {
  const Cmp = name && ICONS[name];
  if (!Cmp) return null;
  return <Cmp size={size} className={className} />;
};

const IconPicker = ({ value, onChange, label = "Icon" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  useDismissable(open, { ref: panelRef, triggerRef, onClose: () => setOpen(false) });

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? NAMES.filter((n) => n.toLowerCase().includes(q)) : NAMES;
  }, [query]);

  return (
    <div className="v2-field v2-iconfield">
      <label>{label}</label>
      <div className="v2-iconpick">
        <button ref={triggerRef} type="button" className="v2-input v2-iconpick-trigger"
                aria-haspopup="true" aria-expanded={open}
                onClick={() => setOpen((v) => !v)}>
          <span className="v2-iconpick-preview">
            {value ? <CategoryIcon name={value} size={16} /> : <span className="v2-iconpick-empty" />}
          </span>
          <span className={value ? "" : "v2-dim"}>{value || "Choose an icon"}</span>
        </button>

        {/* Clearing is its own control rather than a "None" entry in the grid: it is the
            one action someone looks for by shape, not by name. */}
        {value && (
          <button type="button" className="v2-iconpick-clear" aria-label="Clear icon"
                  onClick={() => onChange("")}>
            <TbX size={14} />
          </button>
        )}

        {open && (
          <div ref={panelRef} className="v2-iconpick-panel" role="dialog" aria-label="Choose an icon">
            <input className="v2-input" type="search" value={query} autoFocus
                   placeholder="Search icons" aria-label="Search icons"
                   onChange={(e) => setQuery(e.target.value)} />
            <div className="v2-iconpick-grid">
              {matches.map((n) => {
                const Cmp = ICONS[n];
                return (
                  <button key={n} type="button" title={n} aria-label={n}
                          className={`v2-iconpick-cell${value === n ? " on" : ""}`}
                          onClick={() => { onChange(n); setOpen(false); setQuery(""); }}>
                    <Cmp size={17} />
                  </button>
                );
              })}
              {matches.length === 0 && <p className="v2-hint">Nothing matches “{query}”.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IconPicker;
